/**
 * Real-time Context for MedFast Pharmacy Platform
 * Provides WebSocket integration for live updates and notifications
 */

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { log, LogLevel } from '../utils/logger';
import { errorHandler, createNetworkError } from '../utils/errorHandler';

// WebSocket connection states
export enum ConnectionState {
  CONNECTING = 'connecting',
  CONNECTED = 'connected',
  DISCONNECTED = 'disconnected',
  RECONNECTING = 'reconnecting',
  ERROR = 'error'
}

// Real-time event types
export enum RealtimeEventType {
  // Order events
  ORDER_CREATED = 'order_created',
  ORDER_UPDATED = 'order_updated',
  ORDER_STATUS_CHANGED = 'order_status_changed',
  ORDER_ITEM_PICKED = 'order_item_picked',
  ORDER_ITEM_PACKED = 'order_item_packed',
  
  // Inventory events
  STOCK_UPDATED = 'stock_updated',
  STOCK_LOW_ALERT = 'stock_low_alert',
  STOCK_EXPIRED = 'stock_expired',
  TEMPERATURE_ALERT = 'temperature_alert',
  
  // S8 events
  S8_ENTRY_CREATED = 's8_entry_created',
  S8_ENTRY_UPDATED = 's8_entry_updated',
  S8_RECONCILIATION_REQUIRED = 's8_reconciliation_required',
  
  // Compliance events
  RECALL_NOTICE = 'recall_notice',
  COMPLIANCE_ALERT = 'compliance_alert',
  AUDIT_LOG_CREATED = 'audit_log_created',
  
  // System events
  USER_ONLINE = 'user_online',
  USER_OFFLINE = 'user_offline',
  SYSTEM_MAINTENANCE = 'system_maintenance',
  NOTIFICATION = 'notification'
}

// Real-time event interface
export interface RealtimeEvent {
  id: string;
  type: RealtimeEventType;
  timestamp: string;
  data: any;
  userId?: string;
  branchId?: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
  acknowledged?: boolean;
}

// Notification interface
export interface RealtimeNotification {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error';
  priority: 'low' | 'medium' | 'high' | 'critical';
  timestamp: string;
  read: boolean;
  actionUrl?: string;
  actionText?: string;
  expiresAt?: string;
}

// WebSocket configuration
interface WebSocketConfig {
  url: string;
  reconnectInterval: number;
  maxReconnectAttempts: number;
  heartbeatInterval: number;
  timeout: number;
}

// Real-time context interface
interface RealtimeContextType {
  // Connection state
  connectionState: ConnectionState;
  isConnected: boolean;
  lastConnected?: string;
  reconnectAttempts: number;
  
  // Events
  events: RealtimeEvent[];
  notifications: RealtimeNotification[];
  unreadNotifications: number;
  
  // Methods
  connect: () => void;
  disconnect: () => void;
  sendEvent: (event: Omit<RealtimeEvent, 'id' | 'timestamp'>) => void;
  acknowledgeEvent: (eventId: string) => void;
  markNotificationAsRead: (notificationId: string) => void;
  clearNotifications: () => void;
  
  // Event listeners
  subscribe: (eventType: RealtimeEventType, callback: (event: RealtimeEvent) => void) => () => void;
  unsubscribe: (eventType: RealtimeEventType, callback: (event: RealtimeEvent) => void) => void;
}

// Default configuration
const defaultConfig: WebSocketConfig = {
  url: process.env.REACT_APP_WEBSOCKET_URL || 'ws://localhost:8080/ws',
  reconnectInterval: 5000,
  maxReconnectAttempts: 10,
  heartbeatInterval: 30000,
  timeout: 10000
};

// Create context
const RealtimeContext = createContext<RealtimeContextType | undefined>(undefined);

// Real-time provider component
export const RealtimeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [connectionState, setConnectionState] = useState<ConnectionState>(ConnectionState.DISCONNECTED);
  const [isConnected, setIsConnected] = useState(false);
  const [lastConnected, setLastConnected] = useState<string>();
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const [events, setEvents] = useState<RealtimeEvent[]>([]);
  const [notifications, setNotifications] = useState<RealtimeNotification[]>([]);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  
  const [ws, setWs] = useState<WebSocket | null>(null);
  const [eventListeners, setEventListeners] = useState<Map<RealtimeEventType, Set<(event: RealtimeEvent) => void>>>(new Map());
  const [reconnectTimer, setReconnectTimer] = useState<NodeJS.Timeout | null>(null);
  const [heartbeatTimer, setHeartbeatTimer] = useState<NodeJS.Timeout | null>(null);

  // Connect to WebSocket
  const connect = useCallback(() => {
    if (ws && ws.readyState === WebSocket.OPEN) {
      return;
    }

    setConnectionState(ConnectionState.CONNECTING);
    log.info('Connecting to WebSocket', { url: defaultConfig.url, userId: 'system', component: 'RealtimeContext' });

    try {
      const websocket = new WebSocket(defaultConfig.url);
      
      websocket.onopen = () => {
        setConnectionState(ConnectionState.CONNECTED);
        setIsConnected(true);
        setLastConnected(new Date().toISOString());
        setReconnectAttempts(0);
        setWs(websocket);
        
        log.info('WebSocket connected', { url: defaultConfig.url, userId: 'system', component: 'RealtimeContext' });
        
        // Start heartbeat
        const heartbeat = setInterval(() => {
          if (websocket.readyState === WebSocket.OPEN) {
            websocket.send(JSON.stringify({ type: 'ping' }));
          }
        }, defaultConfig.heartbeatInterval);
        
        setHeartbeatTimer(heartbeat);
      };

      websocket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'pong') {
            // Heartbeat response
            return;
          }
          
          if (data.type === 'event') {
            const realtimeEvent: RealtimeEvent = {
              id: data.id || `event-${Date.now()}`,
              type: data.eventType,
              timestamp: data.timestamp || new Date().toISOString(),
              data: data.data,
              userId: data.userId,
              branchId: data.branchId,
              priority: data.priority || 'medium',
              acknowledged: false
            };
            
            // Add to events list
            setEvents(prev => [realtimeEvent, ...prev.slice(0, 99)]); // Keep last 100 events
            
            // Trigger event listeners
            const listeners = eventListeners.get(realtimeEvent.type);
            if (listeners) {
              listeners.forEach(callback => {
                try {
                  callback(realtimeEvent);
                } catch (error) {
                  log.error('Error in event listener', error as Error, { eventType: realtimeEvent.type, userId: 'system', component: 'RealtimeContext' });
                }
              });
            }
            
            // Create notification for high priority events
            if (realtimeEvent.priority === 'high' || realtimeEvent.priority === 'critical') {
              const notification: RealtimeNotification = {
                id: `notif-${realtimeEvent.id}`,
                title: getNotificationTitle(realtimeEvent.type),
                message: getNotificationMessage(realtimeEvent),
                type: realtimeEvent.priority === 'critical' ? 'error' : 'warning',
                priority: realtimeEvent.priority,
                timestamp: realtimeEvent.timestamp,
                read: false,
                actionUrl: getActionUrl(realtimeEvent.type),
                actionText: getActionText(realtimeEvent.type)
              };
              
              setNotifications(prev => [notification, ...prev.slice(0, 49)]); // Keep last 50 notifications
              setUnreadNotifications(prev => prev + 1);
            }
          }
        } catch (error) {
          log.error('Error parsing WebSocket message', error as Error, { message: event.data, userId: 'system', component: 'RealtimeContext' });
        }
      };

      websocket.onclose = (event) => {
        setConnectionState(ConnectionState.DISCONNECTED);
        setIsConnected(false);
        setWs(null);
        
        // Clear heartbeat
        if (heartbeatTimer) {
          clearInterval(heartbeatTimer);
          setHeartbeatTimer(null);
        }
        
        log.warn('WebSocket disconnected', { 
          code: event.code, 
          reason: event.reason,
          wasClean: event.wasClean 
        });
        
        // Attempt to reconnect if not a clean close
        if (!event.wasClean && reconnectAttempts < defaultConfig.maxReconnectAttempts) {
          setConnectionState(ConnectionState.RECONNECTING);
          setReconnectAttempts(prev => prev + 1);
          
          const timer = setTimeout(() => {
            connect();
          }, defaultConfig.reconnectInterval);
          
          setReconnectTimer(timer);
        }
      };

      websocket.onerror = (error) => {
        setConnectionState(ConnectionState.ERROR);
        log.error('WebSocket error', error as any, { userId: 'system', component: 'RealtimeContext' });
      };

    } catch (error) {
      setConnectionState(ConnectionState.ERROR);
      log.error('Failed to create WebSocket connection', error as Error, { url: defaultConfig.url, userId: 'system', component: 'RealtimeContext' });
    }
  }, [ws, reconnectAttempts, eventListeners, heartbeatTimer]);

  // Disconnect from WebSocket
  const disconnect = useCallback(() => {
    if (ws) {
      ws.close(1000, 'User initiated disconnect');
      setWs(null);
    }
    
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      setReconnectTimer(null);
    }
    
    if (heartbeatTimer) {
      clearInterval(heartbeatTimer);
      setHeartbeatTimer(null);
    }
    
    setConnectionState(ConnectionState.DISCONNECTED);
    setIsConnected(false);
    
    log.info('WebSocket disconnected by user', { userId: 'system', component: 'RealtimeContext' });
  }, [ws, reconnectTimer, heartbeatTimer]);

  // Send event
  const sendEvent = useCallback((event: Omit<RealtimeEvent, 'id' | 'timestamp'>) => {
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      log.warn('Cannot send event - WebSocket not connected', { eventType: event.type, userId: 'system', component: 'RealtimeContext' });
      return;
    }

    const realtimeEvent: RealtimeEvent = {
      ...event,
      id: `event-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date().toISOString()
    };

    try {
      ws.send(JSON.stringify(realtimeEvent));
      
      log.info('Event sent', { eventType: event.type, eventId: realtimeEvent.id, userId: 'system', component: 'RealtimeContext' });
    } catch (error) {
      log.error('Failed to send event', error as Error, { eventType: event.type, userId: 'system', component: 'RealtimeContext' });
    }
  }, [ws]);

  // Acknowledge event
  const acknowledgeEvent = useCallback((eventId: string) => {
    setEvents(prev => prev.map(event => 
      event.id === eventId ? { ...event, acknowledged: true } : event
    ));
    
    log.info('Event acknowledged', { eventId, userId: 'system', component: 'RealtimeContext' });
  }, []);

  // Mark notification as read
  const markNotificationAsRead = useCallback((notificationId: string) => {
    setNotifications(prev => prev.map(notif => 
      notif.id === notificationId ? { ...notif, read: true } : notif
    ));
    
    setUnreadNotifications(prev => Math.max(0, prev - 1));
    
    log.info('Notification marked as read', { notificationId, userId: 'system', component: 'RealtimeContext' });
  }, []);

  // Clear all notifications
  const clearNotifications = useCallback(() => {
    setNotifications([]);
    setUnreadNotifications(0);
    
    log.info('All notifications cleared', { userId: 'system', component: 'RealtimeContext' });
  }, []);

  // Subscribe to event type
  const subscribe = useCallback((eventType: RealtimeEventType, callback: (event: RealtimeEvent) => void) => {
    setEventListeners(prev => {
      const newMap = new Map(prev);
      if (!newMap.has(eventType)) {
        newMap.set(eventType, new Set());
      }
      newMap.get(eventType)!.add(callback);
      return newMap;
    });
    
    log.debug('Subscribed to event type', { eventType, userId: 'system', component: 'RealtimeContext' });
    
    // Return unsubscribe function
    return () => {
      setEventListeners(prev => {
        const newMap = new Map(prev);
        const listeners = newMap.get(eventType);
        if (listeners) {
          listeners.delete(callback);
          if (listeners.size === 0) {
            newMap.delete(eventType);
          }
        }
        return newMap;
      });
      
      log.debug('Unsubscribed from event type', { eventType, userId: 'system', component: 'RealtimeContext' });
    };
  }, []);

  // Unsubscribe from event type
  const unsubscribe = useCallback((eventType: RealtimeEventType, callback: (event: RealtimeEvent) => void) => {
    setEventListeners(prev => {
      const newMap = new Map(prev);
      const listeners = newMap.get(eventType);
      if (listeners) {
        listeners.delete(callback);
        if (listeners.size === 0) {
          newMap.delete(eventType);
        }
      }
      return newMap;
    });
    
    log.debug('Unsubscribed from event type', { eventType, userId: 'system', component: 'RealtimeContext' });
  }, []);

  // Auto-connect on mount
  useEffect(() => {
    connect();
    
    return () => {
      disconnect();
    };
  }, [connect, disconnect]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
      }
      if (heartbeatTimer) {
        clearInterval(heartbeatTimer);
      }
    };
  }, [reconnectTimer, heartbeatTimer]);

  const contextValue: RealtimeContextType = {
    connectionState,
    isConnected,
    lastConnected,
    reconnectAttempts,
    events,
    notifications,
    unreadNotifications,
    connect,
    disconnect,
    sendEvent,
    acknowledgeEvent,
    markNotificationAsRead,
    clearNotifications,
    subscribe,
    unsubscribe
  };

  return (
    <RealtimeContext.Provider value={contextValue}>
      {children}
    </RealtimeContext.Provider>
  );
};

// Hook to use real-time context
export const useRealtime = (): RealtimeContextType => {
  const context = useContext(RealtimeContext);
  if (context === undefined) {
    throw new Error('useRealtime must be used within a RealtimeProvider');
  }
  return context;
};

// Helper functions
const getNotificationTitle = (eventType: RealtimeEventType): string => {
  switch (eventType) {
    case RealtimeEventType.ORDER_CREATED:
      return 'New Order';
    case RealtimeEventType.ORDER_STATUS_CHANGED:
      return 'Order Status Updated';
    case RealtimeEventType.STOCK_LOW_ALERT:
      return 'Low Stock Alert';
    case RealtimeEventType.STOCK_EXPIRED:
      return 'Expired Stock';
    case RealtimeEventType.TEMPERATURE_ALERT:
      return 'Temperature Alert';
    case RealtimeEventType.S8_RECONCILIATION_REQUIRED:
      return 'S8 Reconciliation Required';
    case RealtimeEventType.RECALL_NOTICE:
      return 'Product Recall';
    case RealtimeEventType.COMPLIANCE_ALERT:
      return 'Compliance Alert';
    default:
      return 'System Notification';
  }
};

const getNotificationMessage = (event: RealtimeEvent): string => {
  switch (event.type) {
    case RealtimeEventType.ORDER_CREATED:
      return `New order ${event.data.orderNumber} from ${event.data.customerName}`;
    case RealtimeEventType.ORDER_STATUS_CHANGED:
      return `Order ${event.data.orderNumber} status changed to ${event.data.status}`;
    case RealtimeEventType.STOCK_LOW_ALERT:
      return `Low stock alert for ${event.data.productName} (${event.data.availableQuantity} remaining)`;
    case RealtimeEventType.STOCK_EXPIRED:
      return `Expired stock detected: ${event.data.productName} (Batch: ${event.data.batchLot})`;
    case RealtimeEventType.TEMPERATURE_ALERT:
      return `Temperature excursion: ${event.data.productName} at ${event.data.temperature}°C`;
    case RealtimeEventType.S8_RECONCILIATION_REQUIRED:
      return `S8 reconciliation required for ${event.data.productName}`;
    case RealtimeEventType.RECALL_NOTICE:
      return `Product recall: ${event.data.productName} (${event.data.severity} severity)`;
    case RealtimeEventType.COMPLIANCE_ALERT:
      return `Compliance alert: ${event.data.message}`;
    default:
      return event.data.message || 'System notification';
  }
};

const getActionUrl = (eventType: RealtimeEventType): string | undefined => {
  switch (eventType) {
    case RealtimeEventType.ORDER_CREATED:
    case RealtimeEventType.ORDER_STATUS_CHANGED:
      return '/orders';
    case RealtimeEventType.STOCK_LOW_ALERT:
    case RealtimeEventType.STOCK_EXPIRED:
    case RealtimeEventType.TEMPERATURE_ALERT:
      return '/inventory';
    case RealtimeEventType.S8_RECONCILIATION_REQUIRED:
      return '/s8-register';
    case RealtimeEventType.RECALL_NOTICE:
    case RealtimeEventType.COMPLIANCE_ALERT:
      return '/compliance';
    default:
      return undefined;
  }
};

const getActionText = (eventType: RealtimeEventType): string | undefined => {
  switch (eventType) {
    case RealtimeEventType.ORDER_CREATED:
    case RealtimeEventType.ORDER_STATUS_CHANGED:
      return 'View Orders';
    case RealtimeEventType.STOCK_LOW_ALERT:
    case RealtimeEventType.STOCK_EXPIRED:
    case RealtimeEventType.TEMPERATURE_ALERT:
      return 'View Inventory';
    case RealtimeEventType.S8_RECONCILIATION_REQUIRED:
      return 'View S8 Register';
    case RealtimeEventType.RECALL_NOTICE:
    case RealtimeEventType.COMPLIANCE_ALERT:
      return 'View Compliance';
    default:
      return 'View Details';
  }
};

export default RealtimeContext;
